import React, { useRef } from 'react'
import { Alert, Empty, Spin } from 'antd'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'

import type { PaginationState, VcnManagementBenefitRow } from './types'
import VcnManagementBenefitTable from './VcnManagementBenefitTable'
import {
  ContentArea,
  LoadingOverlay,
  Placeholder,
} from './VcnManagementLayout.styles'

export interface VcnManagementContentProps {
  rows: VcnManagementBenefitRow[]
  isLoading: boolean
  isRefetching: boolean
  error: Error | null
  selectedRows: VcnManagementBenefitRow[]
  selectedRowKeys: string[]
  pagination: PaginationState
  emptyDescription: string
  onRowSelectionChange: (rows: VcnManagementBenefitRow[]) => void
  onPaginationChange: (page: number, pageSize: number) => void
}

export function VcnManagementContent({
  rows,
  isLoading,
  isRefetching,
  error,
  selectedRows,
  selectedRowKeys,
  pagination,
  emptyDescription,
  onRowSelectionChange,
  onPaginationChange,
}: VcnManagementContentProps) {
  const hasEverHadRowsRef = useRef(false)
  const lastNonEmptyRowsRef = useRef<VcnManagementBenefitRow[]>([])
  const hasRows = rows.length > 0
  if (hasRows) {
    hasEverHadRowsRef.current = true
    lastNonEmptyRowsRef.current = rows
  }

  const isInitialLoading =
    isLoading && !isRefetching && !hasEverHadRowsRef.current
  const isBusy = isLoading || isRefetching
  // While busy or errored with no new rows yet, keep the previous page visible under the overlay
  const displayRows =
    (isBusy || error) && !hasRows ? lastNonEmptyRowsRef.current : rows

  if (error && !hasRows && !hasEverHadRowsRef.current) {
    return (
      <ContentArea>
        <Placeholder>
          <p style={{ color: '#ff4d4f', margin: 0 }}>Could not load VCN list</p>
          <p style={{ marginTop: 8 }}>{error.message}</p>
        </Placeholder>
      </ContentArea>
    )
  }

  if (isInitialLoading && !hasRows) {
    return (
      <ContentArea>
        <Placeholder>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 28 }} spin />} />
          <p style={{ marginTop: 16 }}>Loading…</p>
        </Placeholder>
      </ContentArea>
    )
  }

  if (!isBusy && !error && !hasRows) {
    return (
      <ContentArea>
        <Placeholder>
          <Empty description={emptyDescription} />
        </Placeholder>
      </ContentArea>
    )
  }

  return (
    <ContentArea>
      {error && (
        <Alert
          type="warning"
          message="Could not refresh — showing last known data"
          description={error.message}
          style={{ marginBottom: 12 }}
          showIcon
        />
      )}
      {isBusy && (
        <LoadingOverlay>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
        </LoadingOverlay>
      )}
      <VcnManagementBenefitTable
        rows={displayRows}
        emptyText={emptyDescription}
        selectedRows={selectedRows}
        selectedRowKeys={selectedRowKeys}
        pagination={pagination}
        onRowSelectionChange={onRowSelectionChange}
        onPaginationChange={onPaginationChange}
      />
    </ContentArea>
  )
}
